<script setup lang="ts">
import AppContainer from '@apparatix/library/AppContainer.vue';
import { onMounted, ref, computed } from 'vue';
import DataTable, { DataTableRowClickEvent, DataTableFilterEvent } from 'primevue/datatable';
import Column from 'primevue/column';
import FormSkeleton from '@apparatix/Components/FormSkeleton.vue';
import MultiSelect from 'primevue/multiselect';
import Button from 'primevue/button';
import { useColumnConfig, useDocuments, useBuildDefaultColumns } from '@apparatix/composables';
import ToggleSwitch from 'primevue/toggleswitch';
import { useDocumentStore } from '@apparatix/stores/useDocumentStore';
import Badge from 'primevue/badge';
import Avatar from 'primevue/avatar';
import ColumnDisplaySelector from '@apparatix/library/ColumnDisplaySelector.vue';
import Select from 'primevue/select';
import Dropdown from 'primevue/dropdown';
import HeightOptimizer from './HeightOptimizer.vue';
import { useRouter } from 'vue-router';

const store = useDocumentStore();
const {
  loadDocumentsHome,
  updateDocuments,
} = useDocuments();

const router = useRouter();

const dt = ref();

const defaultColumns = useBuildDefaultColumns([
  ['id', 'Id'],
  ['description', 'Name'],
  ['type', 'Type'],
  ['status', 'Status'],
  ['campaignStatus', 'Opportunity Status'],
  ['esignStatus', 'Esign Status'],
  ['entityName', 'Account'],
  ['entityId', 'Account Id', false],
  ['campaignFocus', 'Opportunity Focus', false],
  ['campaignName', 'Opportunity Name'],
  ['age', 'Age'],
  ['created', 'Created'],
  ['modified', 'Modified'],
  ['customerViewed', 'Customer Viewed'],
  ['ownerName', 'Owner'],
]);

const {
  handleColumnOrderChange,
  handleVisibleColumnChange,
  visibleColumns,
  columnOrder,
} = useColumnConfig({
  visibleItemsKey: 'crm:documents:visible-columns',
  columnOrderKey: 'crm:documents:column-order',
  ref: dt,
  defaultColumns: defaultColumns,
});

const editingRowId = ref(null);
let tempStatus = '';

const startEditing = (rowData) => {
  clearTimeout(clickTimeout.value);
  editingRowId.value = rowData.id;
  tempStatus = rowData.status;
};

const clickTimeout = ref(null);
const delay = 250;

const onStatusChange = (rowData) => {
  if (tempStatus === rowData.status) {
    editingRowId.value = null;
    return;
  }
  updateDocuments({ data: { documents: [{ id: rowData.id, status: rowData.status }]}});
  editingRowId.value = null;
};

const onRowClick = (e: DataTableRowClickEvent) => {
  clickTimeout.value = setTimeout(() => {
    store.selectedDocument = e.data;
    router.push({ path: `/crm/documents/detail/document/${e.data.id}` });
    clickTimeout.value = null;
  }, delay);
};

const onRowDoubleClick = (e: DataTableRowClickEvent) => {
  if (clickTimeout.value) {
    clearTimeout(clickTimeout.value);
    clickTimeout.value = null;
  }

  const url = e.data.type === 'Contract'
    ? `/util/get/getContractPDFfromCFS.php?recordId=${e.data.contractId}`
    : `/apx/proposalTool/index-continue.html?advertiser=${e.data.entityId.toString()}&tagid=&pid=${e.data.id}`;

  window.open(url, '_blank'); // Open the link in a new tab
};

const documentOwners = computed(() => {
  const owners = store.documents?.map(contact => contact['ownerName']);
  return [... new Set(owners)];
});

const types = computed(() => {
  const typesMap = store.documents?.map(contact => contact['type']);
  return [... new Set(typesMap)];
});

const esignStatuses = computed(() => {
  const esignStatusesMap = store.documents?.map(contact => contact['esignStatus']);
  return [... new Set(esignStatusesMap)];
});

const statuses = computed(() => {
  const statusesMap = store.documents?.map(contact => contact['status']);
  return [... new Set(statusesMap)];
});

const campaignStatuses = computed(() => {
  const campaignStatusesMap = store.documents?.map(contact => contact['campaignStatus']);
  return [... new Set(campaignStatusesMap)];
});

const exportCSV = () => {
  dt.value.exportCSV();
};

onMounted(() => loadDocumentsHome(false, currentState.value));

const onFilterDocuments = (e: DataTableFilterEvent) => {
  store.filteredDocumentLength = e.filteredValue.length;
};

const onShowFiltersChange = (value: boolean) => {
  if (!value) {
    store.filteredDocumentLength = store.documents.length;
    store.hideTableFilters();
  } else {
    store.showTableFilters();
  }
};

const currentState = ref('Active');
const availableStates = ['Active', 'All'];

const handleStateChange = async () => {
  await loadDocumentsHome(true, currentState.value);
};


const rowClass = (rowData) => rowData.isDocumentAging ? '!text-red-500' : '';

</script>
<template>
  <AppContainer>
    <FormSkeleton v-if="store.isLoading" />
    <template v-else>
      <div class="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex items-center gap-2">
          <Avatar
            icon="pi pi-file"
            size="normal"
            shape="circle"
            style="background-color: var(--p-purple-100); color: var(--p-purple-500);"
          />
          <h1 class="text-lg">
            Documents
          </h1>
          <Badge :value="store.filteredDocumentLength" size="large" severity="secondary" />
        </div>
        <div class="flex flex-col gap-4 sm:flex-row sm:gap-4">
          <Button
            v-tooltip.left="'Export'"
            size="small"
            severity="secondary"
            @click="exportCSV()"
          >
            <i class="pi pi-file-export" /> <span style="font-weight:700;">
              Export
            </span>
          </Button>
        </div>
      </div>
      <div class="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex items-center justify-center gap-2">
          <span class="text-center">Filters</span>
          <ToggleSwitch v-model="store.showFilters" @update:model-value="val => onShowFiltersChange(val)" />
          <Button
            v-tooltip.left="'Clear Filters'"
            size="small"
            outlined
            severity="secondary"
            class="flex items-center"
            :disabled="!(store.showFilters && store.isFilterChanged)"
            @click="store.resetTableFilters"
          >
            <i class="pi pi-filter-slash" />
            <span class="font-bold">Clear Filters</span>
          </Button>
        </div>
        <div class="flex items-center justify-center gap-2 flex-nowrap">
          <ColumnDisplaySelector
            :default-table-columns="defaultColumns"
            :default-visible-columns="defaultColumns.filter(col => col.visible).map(col => col.field)"
            :table-columns="columnOrder"
            :visible-columns="visibleColumns"
            storage-prefix="crm:documents"
            @update:visible-columns="handleVisibleColumnChange"
            @update:table-columns="handleColumnOrderChange"
          />
          <Select
            v-model="currentState"
            fluid
            :options="availableStates"
            class="md:w-14rem"
            @change="handleStateChange"
          >
            <template #value="slotProps">
              <div v-if="slotProps.value" class="flex items-center font-bold">
                <i v-if="slotProps.value === 'All'" class="mr-2 pi pi-circle-fill text-gray-500 bg-gray-200 rounded-full" />
                <i v-else class="mr-2 pi pi-circle-fill text-green-500 bg-green-200 rounded-full" />
                <div>{{ slotProps.value }}</div>
              </div>
            </template>
            <template #option="slotProps">
              <div class="flex items-center">
                <i v-if="slotProps.option == 'All'" class="mr-2 pi pi-circle-fill text-gray-500 bg-gray-200 rounded-full" />
                <i v-else class="mr-2 pi pi-circle-fill text-green-500 bg-green-200 rounded-full" />
                <div>{{ slotProps.option }}</div>
              </div>
            </template>
          </Select>
        </div>
      </div>
      <HeightOptimizer>
        <template #default="{availableHeight}">
          <DataTable
            ref="dt"
            v-model:filters="store.tableFilters"
            :value="store.documents"
            :filter-display="store.showFilters ? 'row' : null"
            :virtual-scroller-options="{itemSize: 50}"
            scrollable
            data-key="id"
            table-style="min-width: 50rem;"
            :scroll-height="availableHeight"
            table-layout="fixed"
            reorderable-columns
            resizable-columns
            :row-class="rowClass"
            @row-click="onRowClick"
            @row-dblclick="onRowDoubleClick"
            @filter="onFilterDocuments"
          >
            <Column
              field="id"
              :header="defaultColumns.find(col => col.field === 'id').header"
              sortable
              :hidden="!visibleColumns.includes('id')"
              :reorderable-column="false"
              style="width: 0px"
            >
              <template #filter="{ filterModel, filterCallback }">
                <InputText
                  v-show="store.showFilters"
                  v-model="filterModel.value"
                  type="text"
                  class="p-column-filter"
                  placeholder="Ids"
                  style="min-width: 8rem; max-width: 8rem"
                  @input="filterCallback()"
                />
              </template>
              <template #body="slotProps">
                <span class="font-semibold">{{ slotProps.data.displayId }}</span>
              </template>
            </Column>
            <Column
              field="description"
              :header="defaultColumns.find(col => col.field === 'description').header"
              sortable
              :hidden="!visibleColumns.includes('description')"
              :reorderable-column="false"
              style="width: 0px"
            >
              <template #filter="{ filterModel, filterCallback }">
                <InputText
                  v-show="store.showFilters"
                  v-model="filterModel.value"
                  type="text"
                  class="p-column-filter"
                  placeholder="Names"
                  style="min-width: 8rem; max-width: 8rem"
                  @input="filterCallback()"
                />
              </template>
              <template #body="slotProps">
                <span class="font-semibold">{{ slotProps.data.description }}</span>
              </template>
            </Column>
            <Column
              field="campaignStatus"
              :header="defaultColumns.find(col => col.field === 'campaignStatus').header"
              sortable
              :hidden="!visibleColumns.includes('campaignStatus')"
              :reorderable-column="false"
              style="width: 0px"
            >
              <template #filter="{ filterModel, filterCallback }">
                <MultiSelect
                  v-show="store.showFilters"
                  v-model="filterModel.value"
                  :options="campaignStatuses"
                  filter
                  placeholder="Statuses"
                  class="p-column-filter"
                  style="min-width: 8rem; max-width: 12rem"
                  :show-clear="true"
                  @change="filterCallback()"
                />
              </template>
            </Column>
            <Column
              field="status"
              :header="defaultColumns.find(col => col.field === 'status').header"
              sortable
              :hidden="!visibleColumns.includes('status')"
              :reorderable-column="false"
              style="width: 0px"
            >
              <template #filter="{ filterModel, filterCallback }">
                <MultiSelect
                  v-show="store.showFilters"
                  v-model="filterModel.value"
                  :options="statuses"
                  filter
                  placeholder="Statuses"
                  class="p-column-filter"
                  style="min-width: 8rem; max-width: 12rem"
                  :show-clear="true"
                  @change="filterCallback()"
                />
              </template>
              <template #body="slotProps">
                <div v-if="editingRowId === slotProps.data.id" @click.stop>
                  <Dropdown
                    v-model="slotProps.data.status"
                    :options="store.documentStatuses"
                    placeholder="Select Status"
                    @change="onStatusChange(slotProps.data)"
                  />
                </div>
                <div v-else @click.stop @dblclick.stop="startEditing(slotProps.data)">
                  {{ slotProps.data.status }}
                </div>
              </template>
            </Column>
            <Column
              field="esignStatus"
              :header="defaultColumns.find(col => col.field === 'esignStatus').header"
              sortable
              :hidden="!visibleColumns.includes('esignStatus')"
              :reorderable-column="false"
              style="width: 0px"
            >
              <template #filter="{ filterModel, filterCallback }">
                <MultiSelect
                  v-show="store.showFilters"
                  v-model="filterModel.value"
                  :options="esignStatuses"
                  filter
                  placeholder="Statuses"
                  class="p-column-filter"
                  style="min-width: 8rem; max-width: 12rem"
                  :show-clear="true"
                  @change="filterCallback()"
                />
              </template>
            </Column>
            <Column
              field="entityName"
              :header="defaultColumns.find(col => col.field === 'entityName').header"
              sortable
              :hidden="!visibleColumns.includes('entityName')"
              :reorderable-column="false"
              style="width: 0px"
            >
              <template #filter="{ filterModel, filterCallback }">
                <InputText
                  v-show="store.showFilters"
                  v-model="filterModel.value"
                  type="text"
                  class="p-column-filter"
                  placeholder="Accounts"
                  style="min-width: 8rem; max-width: 8rem"
                  @input="filterCallback()"
                />
              </template>
            </Column>
            <Column
              field="entityId"
              :header="defaultColumns.find(col => col.field === 'entityId').header"
              sortable
              :hidden="!visibleColumns.includes('entityId')"
              :reorderable-column="false"
              style="width: 0px"
            >
              <template #filter="{ filterModel, filterCallback }">
                <InputText
                  v-show="store.showFilters"
                  v-model="filterModel.value"
                  type="text"
                  class="p-column-filter"
                  placeholder="Account Ids"
                  style="min-width: 8rem; max-width: 8rem"
                  @input="filterCallback()"
                />
              </template>
            </Column>
            <Column
              field="type"
              :header="defaultColumns.find(col => col.field === 'type').header"
              sortable
              :hidden="!visibleColumns.includes('type')"
              :reorderable-column="false"
              style="width: 0px"
            >
              <template #filter="{ filterModel, filterCallback }">
                <MultiSelect
                  v-show="store.showFilters"
                  v-model="filterModel.value"
                  :options="types"
                  filter
                  placeholder="Types"
                  class="p-column-filter"
                  style="min-width: 8rem; max-width: 12rem"
                  :show-clear="true"
                  @change="filterCallback()"
                />
              </template>
            </Column>
            <Column
              field="campaignFocus"
              :header="defaultColumns.find(col => col.field === 'campaignFocus').header"
              sortable
              :hidden="!visibleColumns.includes('campaignFocus')"
              :reorderable-column="false"
              style="width: 0px"
            >
              <template #filter="{ filterModel, filterCallback }">
                <InputText
                  v-show="store.showFilters"
                  v-model="filterModel.value"
                  type="text"
                  class="p-column-filter"
                  placeholder="Focuses"
                  style="min-width: 8rem; max-width: 8rem"
                  @input="filterCallback()"
                />
              </template>
            </Column>
            <Column
              field="campaignName"
              :header="defaultColumns.find(col => col.field === 'campaignName').header"
              sortable
              :hidden="!visibleColumns.includes('campaignName')"
              :reorderable-column="false"
              style="width: 0px"
            >
              <template #filter="{ filterModel, filterCallback }">
                <InputText
                  v-show="store.showFilters"
                  v-model="filterModel.value"
                  type="text"
                  class="p-column-filter"
                  placeholder="Opportunity Names"
                  style="min-width: 8rem; max-width: 8rem"
                  @input="filterCallback()"
                />
              </template>
            </Column>
            <Column
              field="age"
              :header="defaultColumns.find(col => col.field === 'age').header"
              sortable
              :hidden="!visibleColumns.includes('age')"
              :reorderable-column="false"
              style="width: 0px"
            >
              <template #filter="{ filterModel, filterCallback }">
                <InputText
                  v-show="store.showFilters"
                  v-model="filterModel.value"
                  type="text"
                  class="p-column-filter"
                  placeholder="Ages"
                  style="min-width: 4rem; max-width: 4rem"
                  @input="filterCallback()"
                />
              </template>
            </Column>
            <Column
              field="created"
              :header="defaultColumns.find(col => col.field === 'created').header"
              sortable
              :hidden="!visibleColumns.includes('created')"
              :reorderable-column="false"
              style="width: 0px"
            >
              <template #filter="{ filterModel, filterCallback }">
                <InputText
                  v-show="store.showFilters"
                  v-model="filterModel.value"
                  type="text"
                  class="p-column-filter"
                  placeholder="Created"
                  style="min-width: 8rem; max-width: 8rem"
                  @input="filterCallback()"
                />
              </template>
            </Column>
            <Column
              field="modified"
              :header="defaultColumns.find(col => col.field === 'modified').header"
              sortable
              :hidden="!visibleColumns.includes('modified')"
              :reorderable-column="false"
              style="width: 0px"
            >
              <template #filter="{ filterModel, filterCallback }">
                <InputText
                  v-show="store.showFilters"
                  v-model="filterModel.value"
                  type="text"
                  class="p-column-filter"
                  placeholder="Modified"
                  style="min-width: 8rem; max-width: 8rem"
                  @input="filterCallback()"
                />
              </template>
            </Column>
            <Column
              field="customerViewed"
              :header="defaultColumns.find(col => col.field === 'customerViewed').header"
              sortable
              :hidden="!visibleColumns.includes('customerViewed')"
              :reorderable-column="false"
              style="width: 0px"
            >
              <template #filter="{ filterModel, filterCallback }">
                <InputText
                  v-show="store.showFilters"
                  v-model="filterModel.value"
                  type="text"
                  class="p-column-filter"
                  placeholder="Viewed"
                  style="min-width: 8rem; max-width: 8rem"
                  @input="filterCallback()"
                />
              </template>
            </Column>
            <Column
              field="ownerName"
              :header="defaultColumns.find(col => col.field === 'ownerName').header"
              sortable
              :hidden="!visibleColumns.includes('ownerName')"
              :reorderable-column="false"
              style="width: 0px"
            >
              <template #filter="{ filterModel, filterCallback }">
                <MultiSelect
                  v-show="store.showFilters"
                  v-model="filterModel.value"
                  :options="documentOwners"
                  filter
                  placeholder="Owners"
                  class="p-column-filter"
                  style="min-width: 8rem; max-width: 12rem"
                  :show-clear="true"
                  @change="filterCallback()"
                />
              </template>
            </Column>
          </DataTable>
        </template>
      </HeightOptimizer>
    </template>
  </AppContainer>
  <RouterView />
</template>

<style scoped>
.datatable-container {
  /* Ensure the container occupies full height */
  height: 100vh;
  display: flex;
  flex-direction: column;
}

:deep(.p-datatable-header) {
    background: none;
}

.p-button-secondary {
  background-color: lightgray;
  background: var(--white);
  border: 1px solid var(--black-16);
  color: var(--black-80);
}

.p-badge-secondary {
  background-color: lightgray;
  background: var(--white);
  border: 1px solid var(--black-16);
  color: var(--black-80);
}
</style>
